import { useEffect, useRef } from "react";
import { Carousel } from "primereact/carousel";
import { Accordion, AccordionTab } from "primereact/accordion";
import { carouselPt } from "../utils/carouselConfig";
import { Section, renderRichTextContent } from "./sectionHelpers";
import { resolveImageUrl } from "../../../utils/imageUtils";
import "../styles/section-styles.css";
import { Image } from "primereact/image";

/* ─── AccordionSection ─── */
export const AccordionSection = ({ section, index }) => {
  const items = Array.isArray(section?.items) ? section.items : [];
  const contentTemplate = (item) => {
    const scheduleLines = Array.isArray(item?.schedule)
      ? item.schedule : item?.schedule ? [item.schedule] : [];
    const noteLines = Array.isArray(item?.notes) ? item.notes : item?.note ? [item.note] : [];
    const paragraphs = Array.isArray(item?.paragraphs) ? item.paragraphs : item?.paragraph ? [item.paragraph] : [];
    if (paragraphs.length) {
      return <div className="dynamic-accordion-rich-text">{renderRichTextContent(paragraphs)}</div>;
    }
    return (
      <>
        {item?.address ? <p className="dynamic-accordion-address">{item.address}</p> : null}
        {item?.description ? <p className="dynamic-accordion-description">{item.description}</p> : null}
        {scheduleLines.length ? (
          <div className="dynamic-accordion-schedule">
            <strong>{item?.scheduleTitle || "Horario"}</strong>
            <ul>{scheduleLines.map((l, li) => <li key={`${l}-${li}`}>{l}</li>)}</ul>
          </div>
        ) : null}
        {noteLines.length ? (
          <div className="dynamic-accordion-notes">
            {noteLines.map((l, li) => <p key={`${l}-${li}`}>{l}</p>)}
          </div>
        ) : null}
      </>
    );
  };
  return (
    <Section section={section} className="dynamic-section-acordeon" keyName={`accordion-${index}`}>
      <Accordion>
        {items.map((item, idx) => (
          <AccordionTab key={`${item?.title || "acc"}-${idx}`} header={item?.title || `Item ${idx + 1}`}>
            {contentTemplate(item)}
          </AccordionTab>
        ))}
      </Accordion>
    </Section>
  );
};

/* ─── CarruselSection ─── */
export const CarruselSection = ({ section, index }) => {
  const items = Array.isArray(section?.items) ? section.items : [];
  if (!items.length) return null;
  const itemTemplate = (item) => {
    const img = (
      <Image
        src={resolveImageUrl(item.image?.src)}
        alt={item.image?.alt || "Slide"}
      />
    );
    const hasOverlay = item?.title || item?.description;
    const content = hasOverlay ? (
      <div style={{ position: "relative", padding: 0 }}>
        <div className="carrusel-slide-wrapper">{img}</div>
        <div className="carrusel-slide-overlay">
          {item?.title ? <h3 className="carrusel-slide-title">{item.title}</h3> : null}
          {item?.description ? <p className="carrusel-slide-description">{item.description}</p> : null}
        </div>
      </div>
    ) : (
      <div style={{ position: "relative", padding: 0 }}>
        <div className="carrusel-slide-wrapper">{img}</div>
      </div>
    );
    return item.href
      ? <a href={item.href} target="_blank" rel="noreferrer noopener" className="carrusel-slide-link">{content}</a>
      : content;
  };
  return (
    <section key={`carousel-${index}`} className="carrusel-section">
      {section?.title ? <h2>{section.title}</h2> : null}
      <Carousel
        value={items}
        itemTemplate={itemTemplate}
        numVisible={1}
        numScroll={1}
        circular
        autoplayInterval={5000}
        showIndicators
        showNavigators
        pt={carouselPt}
      />
    </section>
  );
};

/* ─── ImageTrackSection ─── */
export const ImageTrackSection = ({ section, items, index }) => {
  const trackRef = useRef(null);
  const isHovering = useRef(false);

  useEffect(() => {
    const el = trackRef.current;
    if (!el || items.length === 0) return;
    let id;
    const step = () => {
      if (!isHovering.current) {
        el.scrollLeft += 1;
        if (el.scrollLeft + el.clientWidth >= el.scrollWidth) {
          el.scrollLeft = 0;
        }
      }
      id = requestAnimationFrame(step);
    };
    id = requestAnimationFrame(step);
    const onEnter = () => { isHovering.current = true; };
    const onLeave = () => { isHovering.current = false; };
    el.addEventListener("mouseenter", onEnter);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      cancelAnimationFrame(id);
      el.removeEventListener("mouseenter", onEnter);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, [items.length]);

  return (
    <Section section={section} keyName={`image-cards-${index}`}>
      <div className="image-track-wrap">
        <div ref={trackRef} className="image-track">
          {items.map((item, idx) => {
            const card = (
              <div key={`img-${idx}`} className={`image-track-item${item.href ? " image-track-item--linkable" : ""}`}>
                <Image src={resolveImageUrl(item.image?.src)} alt={item.image?.alt || ""} />
              </div>
            );
            if (item.href) {
              return (
                <a key={`img-${idx}`} href={item.href} target="_blank" rel="noopener noreferrer" className="image-track-link">
                  {card}
                </a>
              );
            }
            return card;
          })}
        </div>
      </div>
    </Section>
  );
};
